import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Clock, Target, Users, BookOpen, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface InsightData {
  timeSpent: { activity: string; hours: number }[];
  completionPredictions: { courseId: string; title: string; predictedCompletion: number; daysRemaining: number }[];
  peerComparison: { metric: string; userValue: number; peerAverage: number };
  recommendations: string[];
}

export function PersonalInsightsDashboard() {
  const { user } = useAuth();
  const [insights, setInsights] = useState<InsightData | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatingRecommendations, setGeneratingRecommendations] = useState(false);

  useEffect(() => {
    loadInsights();
  }, [user]);

  const loadInsights = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);

      // Get user's enrollments and activity
      const { data: enrollments } = await supabase
        .from('enrollments')
        .select('*, courses(*)')
        .eq('participant_id', user.id);

      // Calculate time spent by activity type
      const { data: activityLogs } = await supabase
        .from('activity_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      const timeByActivity = activityLogs?.reduce((acc: any, log) => {
        const type = log.resource_type || 'other';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});

      const timeSpent = Object.entries(timeByActivity || {}).map(([activity, count]) => ({
        activity,
        hours: Math.round((count as number) * 0.25 * 10) / 10 // Estimate 15 min per activity
      }));

      // Calculate completion predictions for active enrollments
      const activeEnrollments = enrollments?.filter(e => e.status === 'enrolled') || [];
      const completionPredictions = activeEnrollments.map(e => {
        const course = e.courses;
        const enrollmentDays = Math.floor((Date.now() - new Date(e.enrollment_date).getTime()) / (1000 * 60 * 60 * 24));
        const estimatedDuration = course?.duration_hours ? course.duration_hours * 2 : 30; // Estimate days needed
        const progress = Math.min((enrollmentDays / estimatedDuration) * 100, 95);
        const daysRemaining = Math.max(estimatedDuration - enrollmentDays, 1);

        return {
          courseId: e.course_id,
          title: course?.title || 'Unknown Course',
          predictedCompletion: Math.round(progress),
          daysRemaining
        };
      });

      // Get peer comparison data
      const { data: profile } = await supabase
        .from('profiles')
        .select('pd_hours_completed, district_id, job_type')
        .eq('id', user.id)
        .single();

      const { data: peers } = await supabase
        .from('profiles')
        .select('pd_hours_completed')
        .eq('district_id', profile?.district_id)
        .eq('job_type', profile?.job_type)
        .neq('id', user.id);

      const peerAverage = peers?.length 
        ? peers.reduce((sum, p) => sum + (p.pd_hours_completed || 0), 0) / peers.length
        : 0;

      setInsights({
        timeSpent,
        completionPredictions,
        peerComparison: {
          metric: 'PD Hours',
          userValue: profile?.pd_hours_completed || 0,
          peerAverage: Math.round(peerAverage * 10) / 10
        },
        recommendations: []
      });
    } catch (error) {
      console.error('Error loading insights:', error);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const generateRecommendations = async () => {
    if (!user?.id || !insights) return;

    try {
      setGeneratingRecommendations(true);

      const { data: profile } = await supabase
        .from('profiles')
        .select('job_type, department, pd_hours_completed')
        .eq('id', user.id)
        .single();

      const { data: completedCourses } = await supabase
        .from('enrollments')
        .select('courses(title, category_id)')
        .eq('participant_id', user.id)
        .eq('status', 'completed');

      // Call AI for personalized recommendations
      const { data, error } = await supabase.functions.invoke('generate-learning-recommendations', {
        body: {
          profile: {
            role: profile?.job_type,
            department: profile?.department,
            pdHours: profile?.pd_hours_completed
          },
          completedCourses: completedCourses?.map(e => e.courses?.title).filter(Boolean) || [],
          peerComparison: insights.peerComparison
        }
      });

      if (error) throw error;

      setInsights(prev => prev ? { ...prev, recommendations: data.recommendations } : null);
      toast.success('Recommendations generated!');
    } catch (error) {
      console.error('Error generating recommendations:', error);
      toast.error('Failed to generate recommendations');
    } finally {
      setGeneratingRecommendations(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading insights...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <TrendingUp className="h-6 w-6" />
          Personal Learning Insights
        </h2>
      </div>

      {/* Time Spent Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Time Spent (Last 30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {insights?.timeSpent.map(({ activity, hours }) => (
              <div key={activity} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="capitalize">{activity}</span>
                  <span className="font-medium">{hours} hours</span>
                </div>
                <Progress value={(hours / 20) * 100} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Completion Predictions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Course Completion Predictions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {insights?.completionPredictions.map((pred) => (
              <div key={pred.courseId} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{pred.title}</span>
                  <Badge variant="outline">{pred.daysRemaining} days left</Badge>
                </div>
                <Progress value={pred.predictedCompletion} />
                <p className="text-xs text-muted-foreground">
                  {pred.predictedCompletion}% complete
                </p>
              </div>
            ))}
            {insights?.completionPredictions.length === 0 && (
              <p className="text-sm text-muted-foreground">No active courses to track</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Peer Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Peer Comparison (Anonymized)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Your {insights?.peerComparison.metric}</p>
                <p className="text-2xl font-bold">{insights?.peerComparison.userValue}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Peer Average</p>
                <p className="text-2xl font-bold">{insights?.peerComparison.peerAverage}</p>
              </div>
            </div>
            {insights && insights.peerComparison.userValue > insights.peerComparison.peerAverage ? (
              <Badge className="w-full justify-center">Above Average - Keep it up! 🎉</Badge>
            ) : (
              <Badge variant="secondary" className="w-full justify-center">
                Room for growth - You've got this! 💪
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* AI Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Personalized Learning Path
          </CardTitle>
        </CardHeader>
        <CardContent>
          {insights?.recommendations.length === 0 ? (
            <div className="text-center py-8 space-y-4">
              <Sparkles className="h-12 w-12 mx-auto text-muted-foreground" />
              <p className="text-muted-foreground">
                Get AI-powered learning recommendations based on your profile and progress
              </p>
              <Button 
                onClick={generateRecommendations}
                disabled={generatingRecommendations}
              >
                {generatingRecommendations ? 'Generating...' : 'Generate Recommendations'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {insights.recommendations.map((rec, i) => (
                <div key={i} className="p-3 bg-muted rounded-lg text-sm">
                  {rec}
                </div>
              ))}
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={generateRecommendations}
                disabled={generatingRecommendations}
              >
                Refresh Recommendations
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
